import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import dewormingMethods from '../../data/dewormingMethods';
import dewormingTypes from '../../data/dewormingTypes';
import recurrenceOptions from '../../data/recurrenceOptions';
import { useTranslation } from 'react-i18next'; // Import useTranslation hook
import i18n from '../../i18n'; // Import i18n instance
import '../../style/AddDewormingPage.css'; // Import the CSS file

const AddDewormingPage = () => {
    const { id } = useParams(); // Get pet ID from URL
    const navigate = useNavigate(); // For redirection
    const { t } = useTranslation(); // Initialize translation hook
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000'; // Backend URL
    const [newDeworming, setNewDeworming] = useState({
        method: '',
        customMethod: '', // Field for custom method
        type: '',
        lastGivenDate: '',
        recurrence: '',
        endDate: '',
    });
    const [error, setError] = useState(null);

    const handleAddDeworming = async () => {
        try {
            const storedUser = JSON.parse(localStorage.getItem('user')); // Retrieve user data from localStorage
            const token = storedUser?.token;

            if (!token) {
                throw new Error(t('general.noAuthToken')); // Use translation for error
            }

            // If "Other" is selected, use the custom method value in lowercase
            const methodToSend = newDeworming.method === 'other' ? newDeworming.customMethod.toLowerCase() : newDeworming.method;

            // Create the deworming
            const response = await fetch(`${baseUrl}/api/pets/${id}/dewormings`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`, // Include token in Authorization header
                },
                body: JSON.stringify({ ...newDeworming, method: methodToSend }),
            });

            if (!response.ok) {
                throw new Error(t('deworming.createFailed')); // Use translation for error
            }

            const addedDeworming = await response.json(); // Expect the backend to return only the newly created deworming
            console.log('Added Deworming:', addedDeworming); // Debug log

            if (!addedDeworming._id) {
                throw new Error(t('deworming.missingDewormingId')); // Use translation for error
            }

            navigate(`/pet/${id}/deworming/${addedDeworming._id}`); // Redirect to the Deworming Details Page
        } catch (error) {
            setError(error.message);
            console.error(error);
        }
    };

    return (
        <div className="add-deworming-form bg-dafauna-background rounded-xl shadow-md p-8">
            <h3 className="text-dafauna-darkText text-2xl font-bold text-center mb-4">{t('deworming.title')}</h3>
            {error && <p className="error-message text-red-500 text-center">{error}</p>}
            <div className="form-group">
                <label htmlFor="dewormingMethod" className="block text-dafauna-darkText font-medium mb-2">
                    {t('deworming.method')}
                </label>
                <select
                    id="dewormingMethod"
                    value={newDeworming.method}
                    onChange={(e) => setNewDeworming({ ...newDeworming, method: e.target.value })}
                    required
                    className="form-select border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                >
                    <option value="">{t('deworming.selectMethod')}</option>
                    {dewormingMethods.map((method) => (
                        <option key={method.value} value={method.value}>
                            {method.label[i18n.language]} {/* Fetch the label based on the current language */}
                        </option>
                    ))}
                    <option value="other">{t('deworming.other')}</option>
                </select>
            </div>
            {newDeworming.method === 'other' && (
                <div className="form-group">
                    <label htmlFor="customMethod" className="block text-dafauna-darkText font-medium mb-2">
                        {t('deworming.customMethod')}
                    </label>
                    <input
                        type="text"
                        id="customMethod"
                        placeholder={t('deworming.enterCustomMethod')}
                        value={newDeworming.customMethod}
                        onChange={(e) => setNewDeworming({ ...newDeworming, customMethod: e.target.value })}
                        required
                        className="form-input border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                    />
                </div>
            )}
            <div className="form-group">
                <label htmlFor="dewormingType" className="block text-dafauna-darkText font-medium mb-2">
                    {t('deworming.type')}
                </label>
                <select
                    id="dewormingType"
                    value={newDeworming.type}
                    onChange={(e) => setNewDeworming({ ...newDeworming, type: e.target.value })}
                    required
                    className="form-select border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                >
                    <option value="">{t('deworming.selectType')}</option>
                    {dewormingTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                            {type.label[i18n.language]} {/* Fetch the label based on the current language */}
                        </option>
                    ))}
                </select>
            </div>
            <div className="form-group">
                <label htmlFor="lastGivenDate" className="block text-dafauna-darkText font-medium mb-2">
                    {t('deworming.lastGivenDate')}
                </label>
                <input
                    type="date"
                    id="lastGivenDate"
                    placeholder={t('deworming.selectLastGivenDate')}
                    value={newDeworming.lastGivenDate}
                    onChange={(e) => setNewDeworming({ ...newDeworming, lastGivenDate: e.target.value })}
                    required
                    className="form-input border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                />
            </div>
            <div className="form-group">
                <label htmlFor="recurrence" className="block text-dafauna-darkText font-medium mb-2">
                    {t('deworming.recurrence')}
                </label>
                <select
                    id="recurrence"
                    value={newDeworming.recurrence}
                    onChange={(e) => setNewDeworming({ ...newDeworming, recurrence: e.target.value })}
                    required
                    className="form-select border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                >
                    <option value="">{t('deworming.selectRecurrence')}</option>
                    {recurrenceOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label[i18n.language]} {/* Fetch the label based on the current language */}
                        </option>
                    ))}
                </select>
            </div>
            <div className="form-group">
                <label htmlFor="endDate" className="block text-dafauna-darkText font-medium mb-2">
                    {t('deworming.endDate')}
                </label>
                <input
                    type="date"
                    id="endDate"
                    placeholder={t('deworming.selectEndDate')}
                    value={newDeworming.endDate}
                    onChange={(e) => setNewDeworming({ ...newDeworming, endDate: e.target.value })}
                    required
                    className="form-input border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                />
            </div>
            <button
                onClick={handleAddDeworming}
                className="add-deworming-page-btn w-full bg-dafauna-primary text-white rounded-md px-4 py-2 hover:bg-dafauna-primaryLight disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
                {t('deworming.add')}
            </button>
        </div>
    );
};

export default AddDewormingPage;